import { useState, useEffect } from "react";
import { X } from "lucide-react";

export interface PatchTestData {
  id?: number;
  title: string;
  tested: string;
  testedBy: string;
  status: "pending" | "passed" | "failed";
  description: string;
  expiresOn?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PatchTestData) => void;
  onDelete?: (id: number) => void;
  editData?: PatchTestData | null;
  loading?: boolean;
  deleting?: boolean;
}

const StatusIcon = ({ status }: { status: string }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "#f97316";
      case "passed":
        return "#22c55e";
      case "failed":
        return "#ec4899";
      default:
        return "#9ca3af";
    }
  };

  const color = getStatusColor(status);

  return (
    <div className="flex items-center justify-center w-12 h-12 rounded-full" style={{ backgroundColor: `${color}20` }}>
      <svg width="24" height="24" viewBox="0 0 24 24">
        {[0, 1, 2].map((row) =>
          [0, 1, 2].map((col) => {
            const x = 8 + col * 4;
            const y = 8 + row * 4;
            return (
              <circle
                key={`${row}-${col}`}
                cx={x}
                cy={y}
                r="1.5"
                fill={color}
              />
            );
          })
        )}
      </svg>
    </div>
  );
};

export default function PatchTestModal({
  isOpen,
  onClose,
  onSave,
  onDelete,
  editData,
  loading = false,
  deleting = false,
}: Props) {
  const [formData, setFormData] = useState<PatchTestData>({
    title: "",
    tested: "",
    testedBy: "",
    status: "pending",
    description: "",
  });

  useEffect(() => {
    if (editData) {
      setFormData(editData);
    } else {
      // Set default date to today
      const today = new Date();
      const formattedDate = `${today.getDate()} ${today.toLocaleString('default', { month: 'long' })} ${today.getFullYear()}`;
      setFormData({
        title: "",
        tested: formattedDate,
        testedBy: "Amir Mukhtar",
        status: "pending",
        description: "",
      });
    }
  }, [editData, isOpen]);

  useEffect(() => {
    if (formData.tested) {
      // Calculate expiration date (6 months from tested date)
      try {
        const testedDate = new Date(formData.tested);
        const expiryDate = new Date(testedDate);
        expiryDate.setMonth(expiryDate.getMonth() + 6);
        const formatted = `${expiryDate.getDate()} ${expiryDate.toLocaleString('default', { month: 'long' })} ${expiryDate.getFullYear()}`;
        setFormData(prev => ({ ...prev, expiresOn: formatted }));
      } catch (e) {
        // If date parsing fails, just skip
      }
    }
  }, [formData.tested]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  const handleDelete = () => {
    if (editData?.id && onDelete) {
      onDelete(editData.id);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-xl font-bold text-gray-900">Patch test</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-900">
                Title
              </label>
              <span className="text-sm text-gray-500">
                {formData.title.length}/100
              </span>
            </div>
            <input
              type="text"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              placeholder="e.g. Tint patch test"
              maxLength={100}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
              required
            />
          </div>

          {/* Tested & Tested By */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Tested
              </label>
              <input
                type="date"
                value={formData.tested}
                onChange={(e) =>
                  setFormData({ ...formData, tested: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
                required
              />
              {formData.expiresOn && (
                <p className="text-sm text-gray-500 mt-2">
                  Expires on {formData.expiresOn}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Tested by
              </label>
              <input
                type="text"
                value={formData.testedBy}
                onChange={(e) =>
                  setFormData({ ...formData, testedBy: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-3">
              Status
            </label>
            <div className="grid grid-cols-3 gap-4">
              {(["pending", "passed", "failed"] as const).map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setFormData({ ...formData, status })}
                  className={`p-4 border-2 rounded-xl flex flex-col items-center gap-2 transition ${formData.status === status
                    ? "border-purple-500 bg-purple-50"
                    : "border-gray-200 hover:border-gray-300"
                    }`}
                >
                  <StatusIcon status={status} />
                  <span className="text-sm font-medium capitalize">
                    {status}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-900">
                Description
              </label>
              <span className="text-sm text-gray-500">
                {formData.description.length}/200
              </span>
            </div>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Enter the patch test details, e.g. product name or the updates history"
              maxLength={200}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4">
            {editData && onDelete ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className={`w-12 h-12 flex items-center justify-center border rounded-lg transition ${deleting
                  ? "border-gray-300 cursor-not-allowed opacity-50"
                  : "border-gray-300 hover:bg-red-50 hover:border-red-300"
                  }`}
              >
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`px-6 py-2 rounded-lg text-sm font-medium transition ${loading
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-[#885ABB] text-white hover:bg-[#6f47a2]"
                  }`}
              >
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
